/* eslint-disable react/no-unescaped-entities */
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Database, Share2, Lock, User, Mail } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-primary">Privacy Policy</h1>
          <p className="text-slate-500 mt-2">Last updated: March 2026</p>
        </div>

        <div className="space-y-6">
          {/* Introduction */}
          <Card>
            <CardContent className="py-6 text-slate-600">
              <p>
                Prestigious Companies ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use the PTC Client Hub platform.
              </p>
            </CardContent>
          </Card>

          {/* Data Collected */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Database className="h-5 w-5 text-blue-600" />
                Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-4">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Personal Information</h4>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Full name and contact information (email, phone number, address)</li>
                  <li>Date of birth and Social Security Number (for financing applications)</li>
                  <li>Employment information and income details</li>
                  <li>Account credentials (email and encrypted password)</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Financial Information</h4>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Bank account information for payment processing</li>
                  <li>Credit history and credit scores (obtained from credit bureaus)</li>
                  <li>Loan and financing application details</li>
                  <li>Payment history and transaction records</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Technical Information</h4>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Usage data and platform interaction logs</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Data */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-green-600" />
                How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Process and manage financing applications</li>
                <li>Route applications to appropriate lending partners</li>
                <li>Communicate with you about your applications and account</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Maintain and improve our platform and services</li>
                <li>Comply with legal and regulatory requirements</li>
                <li>Prevent fraud and protect against security threats</li>
                <li>Generate anonymized analytics and reports</li>
              </ul>
            </CardContent>
          </Card>

          {/* Information Sharing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Share2 className="h-5 w-5 text-purple-600" />
                Information Sharing
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-4">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">With Lending Partners</h4>
                <p>
                  We share your application information with our network of third-party lenders 
                  for the purpose of processing and evaluating financing applications. This 
                  sharing is necessary to provide our services to you.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">With Service Providers</h4>
                <p>
                  We may share information with trusted service providers who assist us in 
                  operating our platform, including hosting providers, payment processors, 
                  and customer support services. These providers are bound by confidentiality 
                  agreements.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">What We Don't Do</h4>
                <p className="font-semibold text-green-700">
                  We never sell your personal information to third parties for marketing purposes.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Legal Requirements</h4>
                <p>
                  We may disclose information when required by law, court order, or to protect 
                  our rights, property, or safety, or that of our users or others.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="h-5 w-5 text-red-600" />
                Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>We implement robust security measures to protect your information:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>SSL/TLS Encryption:</strong> All data transmitted between your browser and our servers is encrypted</li>
                <li><strong>Secure Storage:</strong> Personal and financial data is stored in encrypted databases with restricted access</li>
                <li><strong>Access Controls:</strong> Only authorized personnel can access sensitive information on a need-to-know basis</li>
                <li><strong>Regular Audits:</strong> We conduct regular security assessments and vulnerability testing</li>
                <li><strong>Password Protection:</strong> All passwords are hashed using industry-standard algorithms (bcrypt)</li>
              </ul>
              <p className="mt-3">
                While we strive to protect your information, no method of transmission over the 
                Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </CardContent>
          </Card>

          {/* User Responsibility */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-amber-600" />
                Your Responsibilities
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>To help protect your account and information:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Keep your login credentials confidential and do not share them with others</li>
                <li>Use strong, unique passwords and change them periodically</li>
                <li>Log out of your account when using shared or public computers</li>
                <li>Notify us immediately if you suspect unauthorized access to your account</li>
                <li>Keep your contact information up to date</li>
              </ul>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5 text-blue-600" />
                Your Privacy Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information (subject to legal requirements)</li>
                <li>Opt out of certain data processing activities</li>
                <li>Receive your data in a portable format</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, please contact us at the email address below.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="h-5 w-5 text-primary" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="text-slate-600 space-y-3">
              <p>
                If you have questions about this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="bg-white p-4 rounded-lg border">
                <p className="font-semibold text-primary">Prestigious Companies</p>
                <p>Privacy Department</p>
                <p className="text-primary font-medium mt-2">support@prestigiouscompanies.org</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8 py-6 border-t border-slate-700">
          <p className="text-sm text-slate-300 font-medium">
            © 2026 PTC Client Hub + EasyQuickFund
          </p>
          <p className="text-xs text-slate-400 mt-1">
            A Prestigious Companies Network | Strategic Partner of the Prestigious Travel Club Foundation
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
